import React from 'react';
import { useIntl } from 'react-intl';
import application from 'services/application';
import useNavigation from 'enhancers/use-navigation';
import Html from 'components/html';
import Button from 'components/button';
import Link from 'components/link';
import type { InputElement } from 'components/input';
import Input from 'components/input';
import is from 'utils/is';

import messages from '../login.i18n';

enum FormField {
  Email = 'email',
  Password = 'password',
}

interface FormSection {
  onAuthRequest: (email: string, password: string) => void;
  error: boolean | undefined;
  loading: boolean | undefined;
}

const Form: React.FunctionComponent<FormSection> = (props) => {
  const { onAuthRequest, error, loading } = props;
  const { formatMessage } = useIntl();
  const [, , link] = useNavigation();
  const [status, setFieldStatus] = React.useState<InputElement['error'] | Record<FormField, InputElement['error']>>(
    error || undefined
  );
  const [formData, setFormData] = React.useState<Record<FormField, string>>({
    email: '',
    password: '',
  } satisfies Record<FormField, string>);

  React.useEffect(() => {
    setFieldStatus((prev) => error || prev);
  }, [error]);

  const handleOnSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    if (!formData.email || !formData.password) {
      setFieldStatus(true);

      return;
    }

    onAuthRequest(formData.email, formData.password);
  };

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = event.target;

    return setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleInputStatus = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = event.target;

    if (!value.trim()) {
      return;
    }

    let newStatus: boolean;

    if (name === FormField.Email) {
      newStatus = !is.email(value);
    } else {
      newStatus = !value.trim();
    }

    setFieldStatus((prev) => {
      if (is.object(prev)) {
        return { ...prev, [name]: newStatus };
      }

      return { email: prev, password: prev, [name]: newStatus };
    });
  };

  const currentProduct = application.current;

  const invalid = {
    email: Boolean(is.object(status) ? status.email : status),
    password: Boolean(is.object(status) ? status.password : status),
  };

  return (
    <Html.form onSubmit={handleOnSubmit}>
      <Input
        type="text"
        name={FormField.Email}
        value={formData.email}
        maxLength={254}
        error={invalid.email}
        onChange={handleOnChange}
        onBlur={handleInputStatus}
        variant="outline"
        label={formatMessage(messages.pages.login.form[currentProduct].emailShort)}
        helperText={invalid.email && formatMessage(messages.pages.login.warning.email)}
      />
      <Input
        type="password"
        name={FormField.Password}
        value={formData.password}
        autoComplete="off"
        maxLength={254}
        error={invalid.password}
        onChange={handleOnChange}
        onBlur={handleInputStatus}
        variant="outline"
        label={formatMessage(messages.pages.login.form[currentProduct].passwordShort)}
        helperText={invalid.password && formatMessage(messages.pages.login.warning.password)}
      />
      <Html.div className="text-center">
        <Button type="submit" disabled={!formData.email || !formData.password} loading={loading}>
          {formatMessage(messages.pages.login.form[currentProduct].login)}
        </Button>
        <Link href={link.recoverPassword()} target="_blank" className="ms-6" variant="primary">
          {formatMessage(messages.pages.login.form[currentProduct].recoverPassword)}
        </Link>
      </Html.div>
    </Html.form>
  );
};

export type { FormSection };
export default Form;
